/**
 * The geometry of the calendar (`CalendarBoard.tsx`), shared by the schedule and the timetable.
 * Pure, so the tests can drive it without a browser.
 */

import { clockOf } from "@teamna/shared";

export const SNAP_MIN = 15;

export function snap(minutes: number, step = SNAP_MIN): number {
  return Math.round(minutes / step) * step;
}

/** "4:45 PM" from minutes after midnight. An `<input type="time">` takes `timeOf` (`16:45`), not this. */
export const timeLabel = clockOf;

/** "4 PM" or "4:45 PM": the hour lines and the narrow blocks of the calendar. */
export function shortTimeLabel(minutes: number): string {
  return clockOf(minutes).replace(":00 ", " ");
}

export type Placed = { key: string; lane: number; of: number; free: boolean; freeLane: number };

/**
 * Where each block sits in a day. Free hours are thin strips at the left (one lane each when they
 * overlap); class blocks share the rest of the width, side by side when they overlap.
 */
export function layoutDay(blocks: Array<{ key: string; start: number; end: number; free: boolean }>): {
  placed: Map<string, Placed>;
  freeLanes: number;
} {
  const placed = new Map<string, Placed>();
  const sorted = [...blocks].sort((a, b) => a.start - b.start || a.end - b.end);

  const freeEnds: number[] = [];
  for (const block of sorted.filter((row) => row.free)) {
    let lane = freeEnds.findIndex((end) => end <= block.start);
    if (lane === -1) {
      lane = freeEnds.length;
      freeEnds.push(block.end);
    } else freeEnds[lane] = block.end;
    placed.set(block.key, { key: block.key, lane: 0, of: 1, free: true, freeLane: lane });
  }

  let cluster: string[] = [];
  let laneEnds: number[] = [];
  let clusterEnd = -1;
  const close = () => {
    for (const key of cluster) placed.get(key)!.of = laneEnds.length;
    cluster = [];
    laneEnds = [];
  };
  for (const block of sorted.filter((row) => !row.free)) {
    if (block.start >= clusterEnd) close();
    let lane = laneEnds.findIndex((end) => end <= block.start);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(block.end);
    } else laneEnds[lane] = block.end;
    placed.set(block.key, { key: block.key, lane, of: 1, free: false, freeLane: 0 });
    cluster.push(block.key);
    clusterEnd = Math.max(clusterEnd, block.end);
  }
  close();

  return { placed, freeLanes: freeEnds.length };
}
